import styles from "./week-schedule.module.css";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Flex, Tag, Tooltip, Table, Popover } from "antd";

import dayjs from "dayjs";

import routes from "@/routes";

import TaskCard from "../cards/TaskCard";

import { STATUS_OPTIONS } from "@/constants/tasks";

const getIcon = (color) => {
  return STATUS_OPTIONS.find((item) => item.value === color)?.icon;
};

const hours = Array.from({ length: 24 }, (_, i) => i);
const weekDays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const WeekSchedule = ({ data, start, statuses, priorities, taskTypes }) => {
  // возвращает список задач разложенных по часам
  const tasksByHours = useMemo(() => {
    const result = Array.from({ length: 24 }, () => []);
    data.forEach((task) => {
      if (!task.due_date && !task.scheduled_due_date) return;

      const dueHour = new Date(
        task.due_date || task.scheduled_due_date
      ).getHours();
      result[dueHour].push(task);
    });
    return result;
  }, [data]);

  const startDate = useMemo(() => {
    return dayjs(start);
  }, [start]);

  const columns = useMemo(() => {
    return [
      {
        title: "",
        dataIndex: "time",
        key: "time",
        width: 60,
      },
      ...weekDays.map((dayName, idx) => {
        const currentDay = startDate.add(idx, "day");
        const isToday = currentDay.isSame(dayjs(), "day");

        return {
          title: (
            <Flex vertical className={isToday ? styles.today : ""}>
              <span style={{ fontSize: 12, color: "#bbbbbb" }}>{dayName}</span>
              <span style={{ fontSize: 15, fontWeight: 500 }}>
                {currentDay.format("D MMMM")}
              </span>
            </Flex>
          ),
          dataIndex: `day${idx}`,
          key: `day${idx}`,
          width: `${100 / 7}%`,
          ellipsis: true,
          render: (tasks) => (
            <>
              {tasks.map((task) => (
                <Popover
                  arrow={false}
                  placement="bottomLeft"
                  key={task.uuid}
                  content={
                    <div
                      style={{
                        width: 370,
                        margin: "-12px",
                      }}
                    >
                      <TaskCard
                        task={task}
                        showActions={false}
                        statuses={statuses}
                        priorities={priorities}
                        taskTypes={taskTypes}
                      />
                    </div>
                  }
                >
                  <Flex
                    key={task.uuid}
                    gap={6}
                    style={{
                      whiteSpace: "nowrap",
                    }}
                    align="center"
                  >
                    <div className={styles.task_status}>
                      <Tooltip
                        title={
                          !task.scheduled_due_date
                            ? task.status?.name
                            : "Планируется"
                        }
                      >
                        <Tag
                          color={
                            !task.scheduled_due_date ? task.status?.color : ""
                          }
                          icon={getIcon(
                            !task.scheduled_due_date
                              ? task.status?.color
                              : "gray"
                          )}
                          className="tag-with-icon-color small no-margin"
                        />
                      </Tooltip>
                    </div>
                    <div className={styles.task_number}>
                      {task.task_number || task?.template?.template_number}
                      {(task.parent || task.template?.parent) &&
                        ` / ${
                          task.parent.task_number ||
                          task.template?.parent?.task_number
                        }`}
                    </div>
                    <div className={styles.task_name}>
                      {!task.scheduled_due_date ? (
                        <Link to={routes.taskDetail(task.uuid)}>
                          {task.name || task.template?.name}
                        </Link>
                      ) : (
                        <div>{task.name || task.template?.name}</div>
                      )}
                    </div>
                  </Flex>
                </Popover>
              ))}
            </>
          ),
        };
      }),
    ];
  }, [startDate, statuses, priorities, taskTypes]);

  const dataSource = useMemo(() => {
    return hours.map((hour) => {
      const tasks = tasksByHours[hour];

      const days = weekDays.reduce((acc, _, idx) => {
        const currentDay = startDate.add(idx, "day");
        acc[`day${idx}`] = tasks.filter((task) => {
          if (!task.due_date && !task.scheduled_due_date) return false;
          const taskDate = dayjs(task.due_date || task.scheduled_due_date);
          return taskDate.isSame(currentDay, "day");
        });
        return acc;
      }, {});
      return {
        key: hour,
        ...days,
        time: `${String(hour).padStart(2, "0")}:00`,
      };
    });
  }, [startDate, tasksByHours]);

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      rowClassName="top-align"
    />
  );
};

export default WeekSchedule;
